import { Group, Material, Mesh, Object3D, Texture } from 'three';
import type { FrameSpec } from '../config/frames';

/**
 * A renderable frame: the root group is posed by the renderer (position +
 * quaternion of the frame origin), hinges rotate for temple spread, and
 * materials fade with the tracking state.
 */
export class FrameObject {
  private currentOpacity = Number.NaN;
  readonly root = new Group();
  private lastSpread = NaN;
  private lastTilt = NaN;

  constructor(
    readonly spec: FrameSpec,
    readonly model: Object3D,
    readonly hingeL: Object3D | null,
    readonly hingeR: Object3D | null,
  ) {
    this.root.name = `frame:${spec.id}`;
    this.root.add(model);
    this.root.traverse((o) => {
      if (o instanceof Mesh) {
        o.frustumCulled = false; // small object, always in view when tracked
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) {
          if (m.userData.baseOpacity === undefined) {
            m.userData.baseOpacity = m.opacity;
            m.userData.baseTransparent = m.transparent;
          }
        }
      }
    });
  }

  /** Opens the temples outwards by `deg` (see FaceMetrics.templeSpreadDeg). */
  setTempleSpread(deg: number): void {
    if (deg === this.lastSpread) return;
    this.lastSpread = deg;
    const rad = (deg * Math.PI) / 180;
    if (this.hingeL) this.hingeL.rotation.y = -rad;
    if (this.hingeR) this.hingeR.rotation.y = rad;
  }

  /**
   * Keeps the temples horizontal in head space: the front is tilted by the
   * pantoscopic angle, the temples are not (the hinge carries the angle), so
   * they rotate back by `-tiltDeg` about the hinge axis. Rotation order YXZ:
   * spread (Y) is applied on top of the tilt (X).
   */
  setTempleTilt(tiltDeg: number): void {
    if (tiltDeg === this.lastTilt) return;
    this.lastTilt = tiltDeg;
    const rad = (-tiltDeg * Math.PI) / 180;
    for (const h of [this.hingeL, this.hingeR]) {
      if (!h) continue;
      h.rotation.order = 'YXZ';
      h.rotation.x = rad;
    }
  }

  /**
   * Global opacity multiplier (fade in/out). A material carrying
   * `userData.onFade(o)` (the library acetate optics) fades itself, in both
   * the composite and the transparent-export modes.
   */
  setOpacity(o: number): void {
    // A110Y: a full traversal per frame is useless while the opacity is constant (the common case);
    // a material change (library acetate) calls invalidateOpacity().
    if (o === this.currentOpacity) return;
    this.currentOpacity = o;
    this.root.visible = o > 0.001;
    this.root.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats as Material[]) {
        const onFade = m.userData.onFade as ((fade: number) => void) | undefined;
        if (onFade) { onFade(o); continue; }
        const base = (m.userData.baseOpacity as number | undefined) ?? 1;
        const baseTransparent = (m.userData.baseTransparent as boolean | undefined) ?? false;
        m.opacity = base * o;
        m.transparent = baseTransparent || o < 0.999;
        m.needsUpdate = false;
      }
    });
  }

  /** The materials changed: the next setOpacity() applies to all of them, and the temple cut is re-attached. */
  invalidateOpacity(): void {
    this.currentOpacity = Number.NaN;
    this.materialsVersion++;
  }

  /** A110Y: bumped whenever the set of materials changes (library acetate, undress). */
  materialsVersion = 0;

  dispose(): void {
    const disposed = new Set<unknown>();
    this.root.traverse((obj) => {
      if (obj instanceof Mesh) {
        if (!disposed.has(obj.geometry)) { obj.geometry.dispose(); disposed.add(obj.geometry); }
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats as Material[]) {
          if (disposed.has(m)) continue;
          for (const value of Object.values(m)) if (value instanceof Texture && !disposed.has(value)) { value.dispose(); disposed.add(value); }
          m.dispose();
          disposed.add(m);
        }
      }
    });
    this.root.removeFromParent();
  }
}
